/**
 * A chart's values as a visually hidden table (RPT-003, A11Y; TMMRSL-0073). The bars stay a
 * `role="img"` with a summary label for sighted users and keep their mouse tooltips; this table,
 * referenced by the chart's `aria-describedby`, gives every bar's label and formatted value to a
 * screen reader without making each bar a tab stop.
 */
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { visuallyHidden } from './visuallyHidden';

export interface ChartDataRow {
  key: string;
  /** The bar's label (its week or period): the row header. */
  label: string;
  /** The bar's values, formatted, one per value column. */
  values: string[];
}

export interface ChartDataTableProps {
  /** The id the chart's `aria-describedby` points at. */
  id: string;
  /** The table's name: the chart's title. */
  label: string;
  /** The label column's header, then one header per value. */
  columns: [string, ...string[]];
  rows: ChartDataRow[];
}

export function ChartDataTable({ id, label, columns, rows }: ChartDataTableProps) {
  const [labelColumn, ...valueColumns] = columns;
  return (
    <Box id={id} sx={visuallyHidden}>
      <Table size="small" aria-label={label}>
        <TableHead>
          <TableRow>
            <TableCell>{labelColumn}</TableCell>
            {valueColumns.map((column) => (
              <TableCell key={column} align="right">
                {column}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key}>
              <TableCell component="th" scope="row">
                {row.label}
              </TableCell>
              {row.values.map((value, index) => (
                <TableCell key={valueColumns[index] ?? index} align="right">
                  {value}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
